import { TinyColor } from '@ctrl/tinycolor';
import { getNiceTickValues } from 'recharts';
import {
    BLUE_300,
    BLUE_700,
    UNKNOWN_CT_BLUE,
    UNKNOWN_CT_BLUE_OPACITY_15,
} from '~styles/colors';
import {
    isThemePalette,
    resolveSeriesColors,
    resolveSingleSeriesColor,
} from './palette-colors';
import type { KpiCardData, KpiPalette } from '../types';

type ColumnsData = Extract<KpiCardData, { form: 'columns' }>;
export type ColumnDatum = ColumnsData['columns'][number];
/** The value-bearing arm: a column whose metric resolved to a real number. */
export type ColumnValueDatum = Extract<ColumnDatum, { value: number }>;

/** Narrows a `ColumnDatum` to its value-bearing arm, mirroring `hasBarValue`. */
export const hasColumnValue = (
    column: ColumnDatum
): column is ColumnValueDatum => !('unavailable' in column);

/**
 * Which label the columns form prints above each bar, in precedence order.
 *
 * `rate` wins when any column carries one: that is the per-round drop-off
 * reading, where the bar IS its own percentage and a count beside it would
 * mislead. `share` is the configured-denominator reading (every column against
 * one base), where both numbers are wanted. `count` is the plain retention
 * reading, unchanged.
 */
export type ColumnsLabelMode = 'rate' | 'share' | 'count';

export const columnsLabelMode = (
    columns: readonly ColumnDatum[]
): ColumnsLabelMode => {
    // Unavailable columns carry neither rate nor share, so they cannot elect a
    // mode; reading them would let one null demote a rate card to `count`.
    const valued = columns.filter(hasColumnValue);
    if (valued.some((c) => c.rate !== undefined)) return 'rate';
    if (valued.some((c) => typeof c.share === 'number')) return 'share';
    return 'count';
};

/**
 * The `count · pct%` label for one share-carrying column. The percentage is a
 * whole percent clamped to 0-100, matching `resolveBars`, so a share outside
 * 0..1 cannot print an impossible figure. A zero count prints as `0 · 0%`
 * rather than vanishing: a column the reader can see is worth a number, and
 * "none of them" is not the same statement as "no data".
 */
export const formatShareLabel = (column: ColumnValueDatum): string => {
    const pct =
        typeof column.share === 'number'
            ? Math.min(100, Math.max(0, Math.round(column.share * 100)))
            : 0;
    return `${column.value.toLocaleString('en-US')} · ${pct}%`;
};

/** Formats a column's sub-label to one decimal place. With the `days` unit,
 * prints a signed day count instead (`+1.4d`, `-0.5d`, `0 d` at zero). */
export const formatSubLabel = (value: number, unit?: 'days'): string => {
    if (unit === 'days') {
        // Rounded before the zero check so a near-zero value (e.g. -0.04)
        // reads as the real zero it is, not a signed "-0.0d".
        const rounded = Math.round(value * 10) / 10;
        if (rounded === 0) return '0 d';
        return `${rounded > 0 ? '+' : ''}${rounded.toFixed(1)}d`;
    }
    return value.toLocaleString('en-US', { maximumFractionDigits: 1 });
};

/** Each available column's value as a whole-percent share of the AVAILABLE
 * columns' own sum, for `shareAxis`; an unavailable column is excluded from
 * that sum (not treated as 0). A zero sum plots every column at 0. */
export const toShareOfTotal = (columns: readonly ColumnDatum[]): number[] => {
    const sum = columns
        .filter(hasColumnValue)
        .reduce((total, column) => total + column.value, 0);
    return columns.map((column) =>
        hasColumnValue(column) && sum > 0 ? (column.value / sum) * 100 : 0
    );
};

/** Column fill colour: for `milestone`, dark blue when `emphasis` is set,
 * light blue otherwise; any other palette reads as one series. */
export const resolveColumnFill = (
    palette?: KpiPalette,
    emphasis?: boolean
): string => {
    if (palette === 'milestone') return emphasis ? BLUE_700 : BLUE_300;
    return resolveSingleSeriesColor(palette);
};

export const resolveColumnFills = (
    columns: readonly ColumnDatum[],
    palette?: KpiPalette
): string[] => {
    const themed = isThemePalette(palette)
        ? resolveSeriesColors(
              columns.map((column) => column.label),
              palette,
              'fill'
          )
        : undefined;
    return (
        themed ??
        columns.map((column) =>
            resolveColumnFill(
                palette,
                hasColumnValue(column) ? column.emphasis : undefined
            )
        )
    );
};

export const resolveRateColors = (
    palette?: KpiPalette
): { label: string; cursor: string } => {
    if (!isThemePalette(palette)) {
        return {
            label: UNKNOWN_CT_BLUE,
            cursor: UNKNOWN_CT_BLUE_OPACITY_15,
        };
    }
    const anchor = resolveSingleSeriesColor(palette);
    return {
        label: anchor,
        cursor: new TinyColor(anchor).setAlpha(0.15).toRgbString(),
    };
};

/** The top tick recharts' `[0, 'auto']` domain would print for `max` (its own
 * nice-tick ladder, not 1/2/5), so a fixed `[0, niceCeiling(max)]` prints the
 * same ticks. 0 stays 0: no ceiling exists for an all-zero chart. */
export const niceCeiling = (max: number, tickCount = 5): number => {
    if (!Number.isFinite(max) || max <= 0) return 0;
    const ticks = getNiceTickValues([0, max], tickCount, false);
    return ticks[ticks.length - 1] ?? max;
};
